"use client";

import type { Dispatch, SetStateAction } from "react";
import Link from "next/link";
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import type { QuizAnswers } from "@/lib/pipeline/style-profile";
import { COLOUR_MOODS } from "@/lib/pipeline/colour-mood";

const STYLE_OPTIONS = [
  { id: "Warm Editorial", label: "Warm Editorial", description: "Rich textures, brass accents, refined neutrals" },
  { id: "Japandi", label: "Japandi", description: "Minimalist oak, clean lines, organic ceramics" },
  { id: "Modern Classic", label: "Modern Classic", description: "Tailored upholstery, panelling, timeless symmetry" },
  { id: "Country Cottage", label: "Country Cottage", description: "Plump seating, botanical prints, rustic timber" },
  { id: "Bold & Eclectic", label: "Bold & Eclectic", description: "Deep jewel tones, statement silhouettes, art" },
];

const QUESTION_COUNT = 4;

const LIFE_FACTORS = [
  { id: "kids", label: "Kids in the home" },
  { id: "pets", label: "Pets (cats/dogs)" },
  { id: "rental", label: "Rental property (no drilling)" },
  { id: "wfh", label: "Work from home setup" },
];

const ELEMENT_OPTIONS: Record<string, { value: string; label: string }[]> = {
  "Bed Frame": ["Single", "Double", "King", "Super King"].map(value => ({ value, label: value })),
  "Bedside Table": ["1", "2"].map(value => ({ value, label: value })),
  Armchair: ["1", "2"].map(value => ({ value, label: value })),
  Sofa: [
    { value: "2 seater", label: "2 Seater" },
    { value: "3 seater", label: "3 Seater" },
    { value: "4 seater", label: "4 Seater" },
    { value: "corner", label: "Corner" },
  ],
};

export function StyleQuiz({ quizAnswers, quizStep, hasBedding, onAnswersChange, onStepChange, onBack, onSubmit }: {
  quizAnswers: QuizAnswers;
  quizStep: number;
  hasBedding: boolean;
  onAnswersChange: Dispatch<SetStateAction<QuizAnswers>>;
  onStepChange: Dispatch<SetStateAction<number>>;
  onBack: () => void;
  onSubmit: () => Promise<void>;
}) {
  function updateElement(name: string, changes: Partial<QuizAnswers["elementsToAdd"][number]>) {
    onAnswersChange(answers => ({
      ...answers,
      elementsToAdd: answers.elementsToAdd.map(element => element.name === name ? { ...element, ...changes } : element),
    }));
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      <div className="flex items-center justify-between">
        <button
          onClick={() => {
            if (quizStep > 1) onStepChange((s) => s - 1);
            else onBack();
          }}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-soft hover:text-ink cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>

        <div className="flex items-center gap-1.5">
          {Array.from({ length: QUESTION_COUNT }, (_, i) => i + 1).map((q) => (
            <span
              key={q}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                quizStep === q
                  ? "bg-clay scale-125"
                  : quizStep > q
                  ? "bg-sage"
                  : "bg-line"
              }`}
            />
          ))}
        </div>
      </div>

      {quizStep === 1 && (
        <div className="space-y-6">
          <div>
            <Badge variant="clay" className="mb-2">Question 1 of {QUESTION_COUNT}</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-ink">
              Which of these feels most like home?
            </h2>
            <p className="text-sm text-ink-soft mt-1">
              Select your primary aesthetic direction.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {STYLE_OPTIONS.map((style) => (
              <button
                key={style.id}
                aria-pressed={quizAnswers.styleAesthetic.includes(style.id)}
                onClick={() =>
                  onAnswersChange((prev) => ({
                    ...prev,
                    styleAesthetic: [style.id],
                  }))
                }
                className={`p-4 rounded-2xl border text-left transition-all cursor-pointer ${
                  quizAnswers.styleAesthetic.includes(style.id)
                    ? "bg-paper border-clay ring-2 ring-clay/40 shadow-sm"
                    : "bg-paper-deep/60 border-line hover:border-ink-soft"
                }`}
              >
                <h4 className="text-base font-bold text-ink">{style.label}</h4>
                <p className="text-xs text-ink-soft mt-1">{style.description}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {quizStep === 2 && (
        <div className="space-y-6">
          <div>
            <Badge variant="clay" className="mb-2">Question 2 of {QUESTION_COUNT}</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-ink">
              How bold would you like the colour?
            </h2>
            <p className="text-sm text-ink-soft mt-1">
              Choose the level of colour and contrast. Swatches are examples; we’ll choose the hues that suit your room.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {COLOUR_MOODS.map((palette) => (
              <button
                key={palette.id}
                aria-pressed={quizAnswers.colorMood === palette.id}
                onClick={() =>
                  onAnswersChange((prev) => ({ ...prev, colorMood: palette.id }))
                }
                className={`p-5 rounded-2xl border text-left transition-all cursor-pointer ${
                  quizAnswers.colorMood === palette.id
                    ? "bg-paper border-clay ring-2 ring-clay/40 shadow-sm"
                    : "bg-paper-deep/60 border-line hover:border-ink-soft"
                }`}
              >
                <div className="flex items-center gap-2 mb-3">
                  {palette.colors.map((c, i) => (
                    <span
                      key={i}
                      className="w-6 h-6 rounded-full border border-line shadow-xs"
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <h4 className="text-base font-bold text-ink">{palette.label}</h4>
                <p className="text-xs text-ink-soft mt-1">{palette.description}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {quizStep === 3 && (
        <div className="space-y-6">
          <div>
            <Badge variant="clay" className="mb-2">Question 3 of {QUESTION_COUNT}</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-ink">
              What goes in the room?
            </h2>
            <p className="text-sm text-ink-soft mt-1">
              Select pieces to add or replace. Existing furniture you leave unchecked will be kept and can be rearranged to suit the room.
            </p>
          </div>

          <div className="space-y-3">
            {quizAnswers.elementsToAdd.map((item) => {
              if (item.name === "Bedding" && !hasBedding) return null;
              return (
                <div key={item.name} className="flex flex-wrap items-center gap-3 bg-paper p-3 rounded-xl border border-line">
                  <input
                    type="checkbox"
                    aria-label={item.name}
                    checked={item.selected}
                    onChange={event => updateElement(item.name, { selected: event.target.checked })}
                    className="w-5 h-5 rounded border-line text-clay focus:ring-clay/40"
                  />
                  <span className="text-sm font-semibold text-ink flex-1 min-w-0">{item.name}</span>
                  {ELEMENT_OPTIONS[item.name] && <select
                    aria-label={item.name === "Bed Frame" ? "Bed size" : `${item.name} options`}
                    value={item.option || ELEMENT_OPTIONS[item.name][0].value}
                    onChange={event => updateElement(item.name, { option: event.target.value })}
                    className="text-xs bg-paper-deep border border-line rounded px-2 py-1 focus:outline-none cursor-pointer"
                  >
                    {ELEMENT_OPTIONS[item.name].map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>}
                  {item.name === "Mattress" && <span className="text-xs text-ink-soft" data-testid="mattress-size">
                    Matches bed · {quizAnswers.elementsToAdd.find(element => element.name === "Bed Frame")?.option || "Select bed size"}
                  </span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {quizStep === QUESTION_COUNT && (
        <div className="space-y-6">
          <div>
            <Badge variant="clay" className="mb-2">Question {QUESTION_COUNT} of {QUESTION_COUNT}</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-ink">
              Any household factors?
            </h2>
            <p className="text-sm text-ink-soft mt-1">
              Helps us filter fabric durability and non-permanent fixtures.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {LIFE_FACTORS.map((factor) => {
              const isSelected = quizAnswers.lifeFactors.includes(factor.id);
              return (
                <button
                  key={factor.id}
                  onClick={() => {
                    onAnswersChange((prev) => ({
                      ...prev,
                      lifeFactors: isSelected
                        ? prev.lifeFactors.filter((f) => f !== factor.id)
                        : [...prev.lifeFactors, factor.id],
                    }));
                  }}
                  className={`p-4 rounded-xl border text-left transition-all cursor-pointer flex items-center justify-between ${
                    isSelected
                      ? "bg-paper border-clay ring-2 ring-clay/40"
                      : "bg-paper-deep/60 border-line hover:border-ink-soft"
                  }`}
                >
                  <span className="text-sm font-semibold text-ink">{factor.label}</span>
                  {isSelected && <CheckCircle2 className="w-4 h-4 text-clay" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex justify-end pt-4">
        {quizStep < QUESTION_COUNT ? (
          <Button size="lg" onClick={() => onStepChange((s) => s + 1)} className="gap-2">
            <span>Next Question</span>
            <ArrowRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button size="lg" onClick={onSubmit} className="gap-2">
            <Sparkles className="w-4 h-4" />
            <span>Generate My Design</span>
          </Button>
        )}
      </div>
      {quizStep === QUESTION_COUNT && <p className="text-xs text-ink-soft">By generating a design, you agree to our <Link href="/terms" className="underline">Terms</Link>. Our <Link href="/privacy" className="underline">Privacy Policy</Link> explains how we use your photo and answers, including sending them to our AI providers.</p>}
    </div>
  );
}
